import React, { useCallback, useEffect, useMemo } from 'react'

import { usePrevious } from '../../../module'
import TimeframeButton from './TimeframeButton'
import { getValidAggregationsForCurrentTimeframe } from './CurrentTimeframe'
import { getValidAggregationsForPreviousTimeframe } from './PreviousTimeframe'
import { getValidAggregationsForHistoricalTimeframe } from './HistoricalTimeframe'
import { getValidAggregationsForQuarterlyTimeframe } from './QuarterlyTimeframe'
import styles from '../_timeframe-filter.module.scss'
import { c } from '../../../translations/LibraryTranslationService'

const Aggregations = ({
  timeframe,
  callout,
  startDate,
  endDate,
  customAggregations,
  updateToValidFilterState,
  trailingTimeframe = false,
  currentPath,
}) => {
  let validAggregations = useMemo(() => [], [])

  const prevLocation = usePrevious(currentPath)

  const getValidAggregationOptions = useCallback(() => {
    // TODO: add the other timeframe types
    let aggregations = []
    if (timeframe.type === 'current') {
      aggregations = getValidAggregationsForCurrentTimeframe(
        timeframe.timeValue,
        customAggregations?.current,
      )
    } else if (timeframe.type === 'previous') {
      aggregations = getValidAggregationsForPreviousTimeframe(
        timeframe.timeValue,
        customAggregations?.previous,
      )
    } else if (
      timeframe.type === 'historical' ||
      timeframe.type === 'trailing'
    ) {
      aggregations = getValidAggregationsForHistoricalTimeframe({
        timeframe,
        startDate,
        endDate,
        customAggregations: trailingTimeframe
          ? customAggregations?.trailing
          : customAggregations?.historical,
        isTimePeriodExtensionEnabled: trailingTimeframe,
      })
    } else {
      // must be quarterly
      aggregations = getValidAggregationsForQuarterlyTimeframe(
        timeframe,
        customAggregations?.quarterly,
      )
    }

    return aggregations
  }, [customAggregations, endDate, startDate, timeframe, trailingTimeframe])

  const updateTimeframe = useCallback(
    (newAggregation) => {
      callout('UPDATE_AGGREGATION', { aggregation: newAggregation })
    },
    [callout],
  )

  // Reset the custom aggregation values in case of path change
  useEffect(() => {
    if (prevLocation !== currentPath ? currentPath : location?.pathname) {
      let validAggregations = getValidAggregationOptions()
      const validAggregationBy = validAggregations?.includes(
        timeframe.aggregation,
      )
        ? timeframe.aggregation
        : validAggregations?.[0]
      updateTimeframe(validAggregationBy)
    }
  }, [
    timeframe.aggregation,
    getValidAggregationOptions,
    updateTimeframe,
    prevLocation,
    currentPath,
  ])

  validAggregations = getValidAggregationOptions()

  /**
   * Below useEffect is used to check if the current aggregation is valid or not
   * If aggregation is not valid then it resets to its default available aggregation
   */
  useEffect(() => {
    if (!validAggregations?.includes(timeframe?.aggregation)) {
      updateToValidFilterState('UPDATE_AGGREGATION', {
        aggregation: validAggregations?.[0],
      })
    }
  }, [timeframe?.aggregation, updateToValidFilterState, validAggregations])

  return (
    <div>
      <div className={styles.aggregationHeader}>View By</div>
      <div className={styles.aggregationDescription}>
        This will aggregate chart data by:
      </div>
      <div className={styles.timeframeFilter}>
        <TimeframeButton
          disabled={!validAggregations?.includes('hour')}
          updateTimeframe={() => updateTimeframe('hour')}
          el={{
            id: 1,
            display: c('hour'),
            timeValue: 'hour',
            type: timeframe?.type,
          }}
          timeframe={timeframe}
          timeframeKey='aggregation'
        />
        <TimeframeButton
          disabled={!validAggregations?.includes('day')}
          updateTimeframe={() => updateTimeframe('day')}
          el={{
            id: 2,
            display: c('day'),
            timeValue: 'day',
            type: timeframe?.type,
          }}
          timeframe={timeframe}
          timeframeKey='aggregation'
        />
        <TimeframeButton
          disabled={!validAggregations?.includes('week')}
          updateTimeframe={() => updateTimeframe('week')}
          el={{
            id: 3,
            display: c('week'),
            timeValue: 'week',
            type: timeframe?.type,
          }}
          timeframe={timeframe}
          timeframeKey='aggregation'
        />
        <TimeframeButton
          disabled={!validAggregations?.includes('month')}
          updateTimeframe={() => updateTimeframe('month')}
          el={{
            id: 4,
            display: c('month'),
            timeValue: 'month',
            type: timeframe?.type,
          }}
          timeframe={timeframe}
          timeframeKey='aggregation'
        />

        <TimeframeButton
          disabled={!validAggregations?.includes('qtr')}
          updateTimeframe={() => updateTimeframe('qtr')}
          el={{
            id: 5,
            display: c('qtr'),
            timeValue: 'quarter',
            QUARTER: true,
            type: timeframe?.type,
          }}
          timeframe={timeframe}
          timeframeKey='aggregation'
        />
      </div>
    </div>
  )
}

export default Aggregations
